import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BaseError } from 'make-error-cause';
import { Button, Paragraph2 } from '@cian/ui-kit';
import { ValidationError } from '@cian/peperrors/shared';

import { ComplaintFeedbackContainer } from '../ComplaintFeedbackContainer';
import { ComplaintForm } from '../../../components/ComplaintForm';
import { ContentRow } from '../../../components/ContentRow';
import { Loader } from '../../../components/Loader';
import { addComplaint, getComplaints, IComplaint } from '../../../api/complaints';
import { useApplicationContext } from '../../../../../../shared/serp/containers/ApplicationContext';
import { useOfferContext } from '../../../context';

interface IComplaintFormContainerProps {
  onClose(): void;
}

export function ComplaintFormContainer({ onClose }: IComplaintFormContainerProps) {
  const { offer, user, api } = useOfferContext();
  const { logger, httpApi } = useApplicationContext();

  const [complaints, setComplaints] = useState<IComplaint[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentComplaint, setSentComplaint] = useState<number | null>(null);
  const [feedbackRequired, setFeedbackRequired] = useState(false);

  const loadComplaints = React.useCallback(async (): Promise<void> => {
    const { geo } = offer;
    const region = geo && geo.address.length > 0 ? geo.address[0].id : 1;

    if (!offer.categoriesIds || offer.categoriesIds.length < 1) {
      const dataError = new ValidationError({
        message: `Failed to get complaints for offer ${offer.cianId || offer.id}: offer not categorized`,
        domain: 'ReportPopupContainer',
      });

      logger.error(dataError);
      setError('Произошла внутренняя ошибка сервиса. Информация об ошибке была направлена нашим разработчикам.');

      return;
    }

    setLoading(true);

    try {
      const complaintsRaw = await getComplaints(httpApi, offer.categoriesIds[offer.categoriesIds.length - 1], region);

      const skipIds = ['89_1089'];

      let complaints = complaintsRaw;

      if (!offer.isByHomeowner) {
        complaints = complaints.filter(item => skipIds.indexOf(item.id) < 0);
      }

      setComplaints(complaints);
    } catch (reason) {
      const dataError = new BaseError(
        `Failed to get complaints for offer ${offer.cianId || offer.id}: offer not categorized`,
        new Error(reason),
      );
      logger.error(dataError);
      setError(
        'Не удалось получить информацию о предложении. Проверьте своё интернет соединение и попробуйте еще раз.',
      );
    }
    setLoading(false);
  }, [offer, httpApi, logger]);

  const showComplaintForm = useMemo(() => !error && !loading && !sentComplaint, [error, loading, sentComplaint]);

  const showSuccessMessage = useMemo(() => !error && !loading && sentComplaint !== null, [
    error,
    loading,
    sentComplaint,
  ]);

  const showError = useMemo(() => !loading && error !== null, [error, loading]);

  const handleSubmitFeedback = useCallback(
    (email: string) => {
      if (sentComplaint) {
        api.sendComplaintFeedback({
          email,
          complaintId: sentComplaint,
        });

        onClose();
      }
    },
    [api.sendComplaintFeedback, sentComplaint],
  );

  const handleSubmitComplaint = useCallback(
    async (complaint: IComplaint, message?: string) => {
      if (!loading) {
        setLoading(true);
        try {
          const res = await addComplaint(httpApi, {
            complaintId: complaint.id,
            message,
            offerId: offer.cianId || offer.id,
          });

          setSentComplaint(Number(res.complaintId));
          setFeedbackRequired(res.getUserFeedback);

          api.onComplaintSent(offer.cianId || offer.id, complaint.name, message);
        } catch (reason) {
          const dataError = new BaseError(`Failed to add complaint for offer ${offer.id}`, new Error(reason));
          logger.error(dataError);
          setError('Не удалось отправить жалобу. Проверьте своё интернет соединение и попробуйте еще раз.');
        }
        setLoading(false);
      }
    },
    [offer, httpApi, logger, loading],
  );

  useEffect(() => {
    if (!complaints) {
      loadComplaints();
    }
  }, []);

  return (
    <>
      {loading && <Loader />}
      {showError && (
        <ContentRow>
          <ContentRow size="L">
            <Paragraph2>{error}</Paragraph2>
          </ContentRow>
          <Button theme="fill_primary" size="XS" onClick={onClose}>
            Закрыть
          </Button>
        </ContentRow>
      )}
      {showComplaintForm && complaints && (
        <ComplaintForm
          offerFullUrl={offer.fullUrl}
          complaints={complaints}
          onSubmit={handleSubmitComplaint}
          onClose={onClose}
        />
      )}

      {showSuccessMessage && (
        <ComplaintFeedbackContainer
          feedbackRequired={feedbackRequired}
          initialEmail={user.isAuthenticated ? user.email : ''}
          onSubmit={handleSubmitFeedback}
          onClose={onClose}
        />
      )}
    </>
  );
}
